import { useEffect, useMemo, useRef, useState } from "react";
import { AtSign, CalendarCheck2, Search, X } from "lucide-react";
import "./HRInterviewSchedulingPage.css";
import {
  getInterviewApplications,
  getInterviewScheduledApplications,
  scheduleInterview,
  scheduleManualInterview,
  updateInterview,
  updateInterviewResult,
  updateManualInterviewResult,
} from "../../services/servicesjobs";
import { getJobs } from "../../services/jobService";

const MERIDIEM_OPTIONS = ["AM", "PM"];
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, index) =>
  String(index + 1).padStart(2, "0"),
);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, index) =>
  String(index).padStart(2, "0"),
);
const ROUND_KEYS = {
  WRITTEN: "WRITTEN",
  TECHNICAL: "TECHNICAL",
  HR: "HR",
};

const ROUND_TYPE_BY_KEY = {
  [ROUND_KEYS.WRITTEN]: "WRITTEN_TEST",
  [ROUND_KEYS.TECHNICAL]: "COMMUNICATION_AND_TECHNICAL_INTERVIEW",
  [ROUND_KEYS.HR]: "HR_INTERVIEW",
};

const ROUND_KEY_BY_TYPE = {
  WRITTEN_TEST: ROUND_KEYS.WRITTEN,
  COMMUNICATION_AND_TECHNICAL_INTERVIEW: ROUND_KEYS.TECHNICAL,
  HR_INTERVIEW: ROUND_KEYS.HR,
};

const NEXT_ROUND_BY_KEY = {
  [ROUND_KEYS.WRITTEN]: ROUND_KEYS.TECHNICAL,
  [ROUND_KEYS.TECHNICAL]: ROUND_KEYS.HR,
  [ROUND_KEYS.HR]: "",
};

const ROUND_RANK_BY_KEY = {
  [ROUND_KEYS.WRITTEN]: 0,
  [ROUND_KEYS.TECHNICAL]: 1,
  [ROUND_KEYS.HR]: 2,
};

const COMMUNICATION_AND_TECHNICAL_ROUND_LABEL =
  "Communication and Technical Round";
const FIRST_ROUND_LABEL = "First Round";

const isManualRoundScheduled = (item, roundKey) => {
  if (!item || item.decision) {
    return false;
  }

  const hasScheduleDetails = Boolean(
    item.interviewId && item.date && item.time,
  );

  if (!hasScheduleDetails) {
    return false;
  }

  if (item.scheduledRound) {
    return item.scheduledRound === roundKey;
  }

  return roundKey === ROUND_KEYS.WRITTEN && item.round === ROUND_KEYS.WRITTEN;
};

const normalizeRoundType = (value) => {
  if (!value) return "";

  const normalized = String(value)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  switch (normalized) {
    case "WRITTEN_TEST":
    case "WRITTENTEST":
    case "WRITTEN":
    case "WRITTEN_TEST_":
      return "WRITTEN_TEST";
    case "COMMUNICATION_AND_TECHNICAL_INTERVIEW":
    case "COMMUNICATIONANDTECHNICALINTERVIEW":
    case "COMMUNICATION_AND_TECHNICAL":
      return "COMMUNICATION_AND_TECHNICAL_INTERVIEW";
    case "HR_INTERVIEW":
    case "HRINTERVIEW":
    case "HR":
      return "HR_INTERVIEW";
    default:
      if (
        normalized.includes("TECHNICAL") &&
        normalized.includes("INTERVIEW")
      ) {
        return "COMMUNICATION_AND_TECHNICAL_INTERVIEW";
      }

      return normalized;
  }
};

const getRoundKeyFromType = (value) =>
  ROUND_KEY_BY_TYPE[normalizeRoundType(value)] || "";

const getLinkLabelForRound = (roundKey) =>
  roundKey === ROUND_KEYS.WRITTEN ? "Exam Link" : "Meeting Link";

const getInterviewLinkForRound = (roundKey, interview = {}) => {
  if (roundKey === ROUND_KEYS.WRITTEN) {
    return (
      interview.examLink || interview.meetingLink || interview.meetLink || ""
    );
  }

  return (
    interview.meetingLink || interview.meetLink || interview.examLink || ""
  );
};

const getInterviewId = (interview = {}) =>
  interview?.interviewId || interview?.id || "";

const getManualCandidateKey = (item = {}) => {
  const email = String(item.email || item.candidateEmail || "")
    .trim()
    .toLowerCase();
  const jobTitle = String(item.jobTitle || "")
    .trim()
    .toLowerCase();

  if (email || jobTitle) {
    return `manual-${email}-${jobTitle}`;
  }

  const fallbackId = item.applicationId || item.id || item.interviewId;

  return fallbackId === null || fallbackId === undefined
    ? ""
    : String(fallbackId);
};

const normalizeScheduledInterviewDate = (value) => {
  if (!value) {
    return "";
  }

  const parsedDate = new Date(value);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toLocaleDateString("en-GB");
  }

  return String(value).split("T")[0] || String(value);
};

const getApplicationInterviews = (item) => {
  const interviews =
    Array.isArray(item?.interviews) && item.interviews.length
      ? item.interviews
      : item?.interview
        ? [item.interview]
        : [];

  const normalizedRoundTypes = interviews
    .map((interview) =>
      normalizeRoundType(interview.roundType || interview.displayRoundType),
    )
    .filter(Boolean);
  const shouldInferRounds =
    interviews.length > 1 &&
    new Set(normalizedRoundTypes).size !== interviews.length;

  return interviews.map((interview, index) => {
    const inferredRoundKey = shouldInferRounds
      ? [ROUND_KEYS.WRITTEN, ROUND_KEYS.TECHNICAL, ROUND_KEYS.HR][index] ||
        ROUND_KEYS.WRITTEN
      : getRoundKeyFromType(interview.roundType || interview.displayRoundType);
    const roundKey = inferredRoundKey || ROUND_KEYS.WRITTEN;

    return {
      ...interview,
      roundKey,
      roundType: ROUND_TYPE_BY_KEY[roundKey],
      date: interview.date || interview.rawDate || "",
      rawDate: interview.rawDate || interview.date || "",
      time: interview.time || interview.scheduledTime || "",
      scheduledDate:
        interview.scheduledDate || interview.rawDate || interview.date || "",
      scheduledTime: interview.scheduledTime || interview.time || "",
      link: getInterviewLinkForRound(roundKey, interview),
    };
  });
};

const getInterviewForRound = (item, roundKey) => {
  const interviews = getApplicationInterviews(item);
  const expectedRoundType = ROUND_TYPE_BY_KEY[roundKey];
  return (
    interviews.find(
      (interview) =>
        normalizeRoundType(interview.roundType) === expectedRoundType,
    ) || null
  );
};

const hasInterviewDetails = (interview) =>
  Boolean(
    interview &&
    (interview.interviewId ||
      interview.rawDate ||
      interview.scheduledDate ||
      interview.date ||
      interview.time ||
      interview.scheduledTime ||
      interview.type ||
      interview.link ||
      interview.meetLink ||
      interview.examLink ||
      interview.location),
  );

const getCurrentRoundKeyForApplication = (item) => {
  const stage = getRoundKeyFromType(item?.currentInterviewStage);
  if (stage) {
    return stage;
  }

  const written = getInterviewForRound(item, ROUND_KEYS.WRITTEN);
  const technical = getInterviewForRound(item, ROUND_KEYS.TECHNICAL);
  const hr = getInterviewForRound(item, ROUND_KEYS.HR);

  if (hr) {
    return ROUND_KEYS.HR;
  }

  if (technical) {
    return technical.result === "SELECTED"
      ? ROUND_KEYS.HR
      : ROUND_KEYS.TECHNICAL;
  }

  if (written) {
    return written.result === "SELECTED"
      ? ROUND_KEYS.TECHNICAL
      : ROUND_KEYS.WRITTEN;
  }

  return ROUND_KEYS.WRITTEN;
};

const getCurrentRoundDecision = (item, roundKey) => {
  const interview = getInterviewForRound(item, roundKey);
  if (!interview) {
    return "";
  }

  if (roundKey === ROUND_KEYS.HR) {
    return interview.result || "";
  }

  return interview.result === "REJECTED" ? "REJECTED" : "";
};

const getRoundStatePatch = (roundKey, interview) => {
  if (!roundKey || !interview) {
    return null;
  }

  if (roundKey === ROUND_KEYS.WRITTEN) {
    return {
      writtenInterviewScheduled: true,
      writtenInterviewId: getInterviewId(interview),
      writtenInterviewDate: interview.rawDate || interview.scheduledDate || "",
      writtenInterviewTime: interview.time || interview.scheduledTime || "",
      writtenInterviewType: interview.type || "",
      writtenInterviewLink: getInterviewLinkForRound(roundKey, interview),
      writtenInterviewLocation: interview.location || "",
    };
  }

  if (roundKey === ROUND_KEYS.TECHNICAL) {
    return {
      technicalInterviewScheduled: true,
      technicalInterviewId: getInterviewId(interview),
      technicalInterviewDate:
        interview.rawDate || interview.scheduledDate || "",
      technicalInterviewTime: interview.time || interview.scheduledTime || "",
      technicalInterviewType: interview.type || "",
      technicalInterviewLink: getInterviewLinkForRound(roundKey, interview),
      technicalInterviewLocation: interview.location || "",
    };
  }

  if (roundKey === ROUND_KEYS.HR) {
    return {
      hrInterviewScheduled: true,
      hrInterviewId: getInterviewId(interview),
      hrInterviewDate: interview.rawDate || interview.scheduledDate || "",
      hrInterviewTime: interview.time || interview.scheduledTime || "",
      hrInterviewType: interview.type || "",
      hrInterviewLink: getInterviewLinkForRound(roundKey, interview),
      hrInterviewLocation: interview.location || "",
    };
  }

  return null;
};

const deriveManualPipelineEntries = (manualRecords = []) => {
  const groupedRecords = new Map();

  manualRecords.forEach((record) => {
    const key = getManualCandidateKey(record);

    if (!key) {
      return;
    }

    const existing = groupedRecords.get(key) || [];
    existing.push(record);
    groupedRecords.set(key, existing);
  });

  return Array.from(groupedRecords.entries()).map(([candidateKey, records]) => {
    const sortedRecords = [...records].sort((left, right) => {
      const leftRank =
        ROUND_RANK_BY_KEY[left.scheduledRound || left.round] ?? 0;
      const rightRank =
        ROUND_RANK_BY_KEY[right.scheduledRound || right.round] ?? 0;

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      return Number(left.updatedAt || 0) - Number(right.updatedAt || 0);
    });

    const byRound = sortedRecords.reduce((rounds, record) => {
      const roundKey =
        record.scheduledRound || record.round || ROUND_KEYS.WRITTEN;
      const current = rounds[roundKey];

      if (
        !current ||
        Number(record.updatedAt || 0) >= Number(current.updatedAt || 0)
      ) {
        rounds[roundKey] = record;
      }

      return rounds;
    }, {});

    let currentRound = ROUND_KEYS.WRITTEN;
    let decision = "";

    [ROUND_KEYS.WRITTEN, ROUND_KEYS.TECHNICAL, ROUND_KEYS.HR].forEach(
      (roundKey) => {
        const record = byRound[roundKey];
        const result = String(record?.result || "").toUpperCase();

        if (!record) {
          return;
        }

        currentRound = roundKey;

        if (result === "SELECTED") {
          const nextRound = NEXT_ROUND_BY_KEY[roundKey];
          currentRound = nextRound || roundKey;
          decision = nextRound ? "" : "SELECTED";
        } else if (result === "REJECTED") {
          decision = "REJECTED";
        } else {
          decision = "";
        }
      },
    );

    const latestRecord = sortedRecords[sortedRecords.length - 1] || {};

    return {
      applicationId: candidateKey,
      fullName: latestRecord.fullName || "Candidate",
      email: latestRecord.email || "",
      jobTitle: latestRecord.jobTitle || "",
      status: "MANUAL",
      round: currentRound,
      decision,
      isManualSchedule: true,
      writtenRecord: byRound[ROUND_KEYS.WRITTEN] || null,
      technicalRecord: byRound[ROUND_KEYS.TECHNICAL] || null,
      hrRecord: byRound[ROUND_KEYS.HR] || null,
      updatedAt: Math.max(
        ...sortedRecords.map((record) => Number(record.updatedAt || 0)),
        0,
      ),
    };
  });
};
const PIPELINE_COLUMNS = [
  {
    key: ROUND_KEYS.WRITTEN,
    title: FIRST_ROUND_LABEL,
    description: `Schedule and shortlist candidates for ${COMMUNICATION_AND_TECHNICAL_ROUND_LABEL.toLowerCase()}.`,
  },
  {
    key: ROUND_KEYS.TECHNICAL,
    title: COMMUNICATION_AND_TECHNICAL_ROUND_LABEL,
    description: "Schedule and shortlist candidates for HR round.",
  },
  {
    key: ROUND_KEYS.HR,
    title: "HR Round",
    description: "Schedule the final meeting and mark selected or rejected.",
  },
];

const normalizeCandidateId = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
};

const parseEmailList = (value) =>
  String(value || "")
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter(Boolean);

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const mergeEmailValues = (committedValue, draftValue) =>
  parseEmailList(`${committedValue || ""} ${draftValue || ""}`).join(", ");

const getTimeParts = (value) => {
  const normalized = String(value || "").trim();

  const twelveHourMatch = normalized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (twelveHourMatch) {
    return {
      hour: twelveHourMatch[1].padStart(2, "0"),
      minute: twelveHourMatch[2],
      meridiem: twelveHourMatch[3].toUpperCase(),
    };
  }

  const twentyFourHourMatch = normalized.match(/^(\d{1,2}):(\d{2})$/);

  if (!twentyFourHourMatch) {
    return {
      hour: "",
      minute: "",
      meridiem: "",
    };
  }

  const rawHour = Number(twentyFourHourMatch[1]);
  const minute = twentyFourHourMatch[2];

  if (Number.isNaN(rawHour) || rawHour < 0 || rawHour > 23) {
    return {
      hour: "",
      minute: "",
      meridiem: "",
    };
  }

  return {
    hour: String(rawHour % 12 || 12).padStart(2, "0"),
    minute,
    meridiem: rawHour >= 12 ? "PM" : "AM",
  };
};

const formatInterviewTime = (value) => {
  const parts = getTimeParts(value);

  if (!parts.hour || !parts.minute || !parts.meridiem) {
    return String(value || "");
  }

  return `${parts.hour}:${parts.minute} ${parts.meridiem}`;
};

const getDateTimeValue = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) {
    return null;
  }

  const parts = getTimeParts(timeValue);

  if (!parts.hour || !parts.minute || !parts.meridiem) {
    return null;
  }

  const hours24 =
    (Number(parts.hour) % 12) + (parts.meridiem === "PM" ? 12 : 0);
  const rawDate = String(dateValue).trim();
  let normalizedDate = null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
    normalizedDate = new Date(`${rawDate}T00:00:00`);
  } else if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(rawDate)) {
    const [first, second, year] = rawDate.split(/[-/]/).map(Number);
    normalizedDate = new Date(year, second - 1, first);
  } else {
    normalizedDate = new Date(rawDate);
  }

  if (Number.isNaN(normalizedDate.getTime())) {
    return null;
  }

  normalizedDate.setHours(hours24, Number(parts.minute), 0, 0);
  return normalizedDate;
};

const DropdownTextInput = ({
  name,
  value,
  placeholder,
  options,
  onChange,
  hasError,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = useMemo(() => {
    const keyword = String(value || "")
      .trim()
      .toLowerCase();

    if (!keyword) {
      return options;
    }

    return options.filter((option) => option.toLowerCase().includes(keyword));
  }, [options, value]);

  return (
    <div className="time-dropdown-input-wrap">
      <input
        type="text"
        name={name}
        autoComplete="off"
        inputMode="numeric"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          setTimeout(() => {
            setIsOpen(false);
          }, 160);
        }}
        className={`time-entry-input ${hasError ? "input-error" : ""}`}
      />
      <div className={`time-dropdown-menu ${isOpen ? "show" : ""}`}>
        {filteredOptions.map((option) => (
          <button
            key={option}
            type="button"
            className="time-dropdown-option"
            onMouseDown={(e) => {
              e.preventDefault();
              onChange({
                target: {
                  name,
                  value: option,
                },
              });
              setIsOpen(false);
            }}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

const ManualEmailChipsInput = ({
  value,
  draftValue,
  onDraftChange,
  onChange,
  hasError,
}) => {
  const emails = parseEmailList(value);

  const commitDraft = (nextDraft = draftValue) => {
    const nextEmails = parseEmailList(nextDraft);

    if (!nextEmails.length) {
      onDraftChange("");
      return;
    }

    const mergedEmails = Array.from(new Set([...emails, ...nextEmails]));
    onChange({
      target: {
        name: "email",
        value: mergedEmails.join(", "),
      },
    });
    onDraftChange("");
  };

  const removeEmail = (emailToRemove) => {
    onChange({
      target: {
        name: "email",
        value: emails.filter((email) => email !== emailToRemove).join(", "),
      },
    });
  };

  return (
    <div className={`manual-email-input ${hasError ? "input-error" : ""}`}>
      {emails.map((email) => (
        <span className="manual-email-chip" key={email}>
          {email}
          <button
            type="button"
            aria-label={`Remove ${email}`}
            onClick={() => removeEmail(email)}
          >
            <X size={14} />
          </button>
        </span>
      ))}
      <input
        type="text"
        autoComplete="off"
        spellCheck="false"
        value={draftValue}
        onChange={(e) => {
          const nextValue = e.target.value;

          if (/[,;\s]$/.test(nextValue)) {
            commitDraft(nextValue);
            return;
          }

          onDraftChange(nextValue);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Tab") {
            if (draftValue.trim()) {
              e.preventDefault();
              commitDraft();
            }
          }

          if (e.key === "Backspace" && !draftValue && emails.length) {
            removeEmail(emails[emails.length - 1]);
          }
        }}
        onPaste={(e) => {
          const pastedText = e.clipboardData.getData("text");

          if (parseEmailList(pastedText).length > 1) {
            e.preventDefault();
            commitDraft(pastedText);
          }
        }}
        onBlur={() => commitDraft()}
        placeholder={
          emails.length ? "Add another email..." : "Add candidate emails..."
        }
      />
    </div>
  );
};

const HRInterviewSchedulingPage = () => {
  const candidateInputWrapRef = useRef(null);
  const isCandidateMenuInteractionRef = useRef(false);
  const lastCandidateMenuInteractionRef = useRef(0);
  const [applicationManagementCandidates, setApplications] = useState([]);
  const [scheduledInterviews, setScheduledInterviews] = useState([]);
  const [pendingScheduledIds, setPendingScheduledIds] = useState({});
  const [scheduleError, setScheduleError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSchedulePopupOpen, setIsSchedulePopupOpen] = useState(false);
  const [schedulePopupTitle, setSchedulePopupTitle] = useState(
    "Interview Scheduled",
  );
  const [schedulePopupMessage, setSchedulePopupMessage] = useState("");
  const [isCandidateMenuOpen, setIsCandidateMenuOpen] = useState(false);
  const [pipelineState, setPipelineState] = useState({});
  const [manualInterviews, setManualInterviews] = useState([]);
  const [jobOptions, setJobOptions] = useState([]);
  const [manualEmailDraft, setManualEmailDraft] = useState("");
  const [roundSearchTerms, setRoundSearchTerms] = useState({
    [ROUND_KEYS.WRITTEN]: "",
    [ROUND_KEYS.TECHNICAL]: "",
    [ROUND_KEYS.HR]: "",
  });
  const [form, setForm] = useState({
    interviewId: "",
    applicationId: "",
    schedulingRound: "",
    isManualSchedule: false,
    jobTitle: "",
    fullName: "",
    email: "",
    date: "",
    hour: "",
    minute: "",
    meridiem: "AM",
    useGoogleMeet: true,
    meetLink: "",
    location: "",
  });

  const fetchApplications = async () => {
    try {
      const applicationList = await getInterviewApplications();
      setApplications(applicationList);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
      setApplications([]);
    }
  };

  const fetchScheduledInterviews = async () => {
    try {
      const data = await getInterviewScheduledApplications();
      const formatted = data.flatMap((app) => {
        const interviews =
          Array.isArray(app.interviews) && app.interviews.length
            ? app.interviews
            : app.interview
              ? [app.interview]
              : [];

        return interviews.map((interview) => ({
          id: `${app.id}-${interview.id || interview.roundType || "interview"}`,
          interviewId: interview.id || "",
          applicationId: app.applicationId || app.id,
          fullName: app.fullName,
          email: app.email,
          roundType: normalizeRoundType(interview.roundType),
          rawDate: interview.date ? String(interview.date).split("T")[0] : "",
          scheduledDate: interview.date
            ? String(interview.date).split("T")[0]
            : "",
          date: normalizeScheduledInterviewDate(interview.date),
          scheduledTime: formatInterviewTime(interview.time || ""),
          time: formatInterviewTime(interview.time || ""),
          type: interview.interviewType || "",
          status: interview.status || "",
          result: interview.result || "",
          meetLink: getInterviewLinkForRound(
            getRoundKeyFromType(interview.roundType),
            interview,
          ),
          location: interview.location || "",
        }));
      });

      setScheduledInterviews(formatted);

      const manualRecordsFromApi = data.flatMap((app) => {
        const interviews = Array.isArray(app.interviews) ? app.interviews : [];

        return interviews
          .filter((interview) => interview?.source === "manual")
          .map((interview) => ({
            applicationId: getManualCandidateKey({
              email:
                app.email || interview.email || interview.candidateEmail || "",
              jobTitle: interview.jobTitle || app.jobTitle || "",
              id: app.applicationId || app.id || interview.id || "",
            }),
            interviewId: interview.id || "",
            fullName: app.fullName || interview.fullName || "Candidate",
            email:
              app.email || interview.email || interview.candidateEmail || "",
            jobTitle: interview.jobTitle || app.jobTitle || "",
            round: getRoundKeyFromType(interview.roundType),
            scheduledRound: getRoundKeyFromType(interview.roundType),
            date:
              interview.rawDate || normalizeDateForInput(interview.date || ""),
            time: formatInterviewTime(interview.time || ""),
            type: interview.interviewType || "",
            meetLink: interview.meetingLink || interview.examLink || "",
            location: interview.location || "",
            status: interview.status || "",
            result: interview.result || "",
            completedAt: interview.completedAt || "",
            updatedAt: new Date(
              interview.updatedAt || interview.createdAt || Date.now(),
            ).getTime(),
          }));
      });

      setManualInterviews(deriveManualPipelineEntries(manualRecordsFromApi));
    } catch (error) {
      console.error("Error fetching scheduled interviews:", error);
      setScheduledInterviews([]);
    }
  };

  const fetchJobOptions = async () => {
    try {
      const response = await getJobs({ limit: 1000, is_active: true });
      const jobs = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.jobs)
          ? response.jobs
          : [];

      setJobOptions(
        jobs
          .filter((job) => job?.is_active !== false)
          .map((job) => job.title)
          .filter(Boolean),
      );
    } catch (error) {
      console.error("Failed to fetch manual interview job titles:", error);
      setJobOptions([]);
    }
  };

  useEffect(() => {
    fetchApplications();
    fetchScheduledInterviews();
    fetchJobOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!scheduledInterviews.length) {
      return;
    }

    setPipelineState((prev) => {
      let hasChanges = false;
      const nextState = { ...prev };

      scheduledInterviews.forEach((interview) => {
        const applicationId = normalizeCandidateId(
          interview.applicationId || interview.id,
        );
        const roundKey = getRoundKeyFromType(interview.roundType);
        const patch = getRoundStatePatch(roundKey, interview);

        if (!applicationId || !patch) {
          return;
        }

        const currentState = nextState[applicationId] || {};
        const mergedState = {
          ...currentState,
          ...patch,
        };

        const patchKeys = Object.keys(patch);
        const changed = patchKeys.some(
          (key) =>
            String(currentState[key] || "") !== String(mergedState[key] || ""),
        );

        if (changed) {
          nextState[applicationId] = {
            ...mergedState,
            updatedAt: currentState.updatedAt || Date.now(),
          };
          hasChanges = true;
        }
      });

      return hasChanges ? nextState : prev;
    });
  }, [scheduledInterviews]);

  useEffect(() => {
    if (!isCandidateMenuOpen) {
      return undefined;
    }

    let animationFrameId = 0;

    const closeCandidateMenu = (event) => {
      if (
        event?.target &&
        candidateInputWrapRef.current?.contains(event.target)
      ) {
        return;
      }

      setIsCandidateMenuOpen(false);
    };

    const getCurrentMetrics = () => {
      const rect = candidateInputWrapRef.current?.getBoundingClientRect();

      return {
        top: rect?.top ?? 0,
        left: rect?.left ?? 0,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
      };
    };

    let previousMetrics = getCurrentMetrics();

    const watchMenuPosition = () => {
      const nextMetrics = getCurrentMetrics();
      const hasMoved =
        Math.abs(nextMetrics.top - previousMetrics.top) > 1 ||
        Math.abs(nextMetrics.left - previousMetrics.left) > 1 ||
        nextMetrics.scrollX !== previousMetrics.scrollX ||
        nextMetrics.scrollY !== previousMetrics.scrollY;

      if (hasMoved) {
        closeCandidateMenu();
        return;
      }

      previousMetrics = nextMetrics;
      animationFrameId = window.requestAnimationFrame(watchMenuPosition);
    };

    window.addEventListener("scroll", closeCandidateMenu, true);
    window.addEventListener("wheel", closeCandidateMenu, { passive: true });
    window.addEventListener("touchmove", closeCandidateMenu, { passive: true });
    window.addEventListener("resize", closeCandidateMenu);
    document.addEventListener("scroll", closeCandidateMenu, true);
    document.addEventListener("wheel", closeCandidateMenu, true);
    document.addEventListener("touchmove", closeCandidateMenu, true);
    window.visualViewport?.addEventListener("scroll", closeCandidateMenu);
    animationFrameId = window.requestAnimationFrame(watchMenuPosition);

    return () => {
      window.removeEventListener("scroll", closeCandidateMenu, true);
      window.removeEventListener("wheel", closeCandidateMenu);
      window.removeEventListener("touchmove", closeCandidateMenu);
      window.removeEventListener("resize", closeCandidateMenu);
      document.removeEventListener("scroll", closeCandidateMenu, true);
      document.removeEventListener("wheel", closeCandidateMenu, true);
      document.removeEventListener("touchmove", closeCandidateMenu, true);
      window.visualViewport?.removeEventListener("scroll", closeCandidateMenu);
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [isCandidateMenuOpen]);

  const noteCandidateMenuInteraction = () => {
    isCandidateMenuInteractionRef.current = true;
    lastCandidateMenuInteractionRef.current = Date.now();
  };

  const releaseCandidateMenuInteraction = () => {
    window.setTimeout(() => {
      isCandidateMenuInteractionRef.current = false;
    }, 220);
  };

  const getTodayDateValue = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getSafeDisplayDate = (value) => {
    if (!value) return "";
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }
    return parsedDate.toLocaleDateString("en-GB");
  };

  const normalizeDateForInput = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      const trimmed = value.trim();

      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }

      const isoMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})T/);
      if (isoMatch) {
        return isoMatch[1];
      }
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return String(value);
    }

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const candidateDirectory = useMemo(() => {
    return applicationManagementCandidates.map((item) => ({
      applicationId: item.applicationId || item.id,
      fullName: item.fullName,
      email: item.email,
      status: item.status,
    }));
  }, [applicationManagementCandidates]);

  const findCandidateEmail = (candidateName) => {
    if (!candidateName) {
      return "";
    }

    const exactMatch = candidateDirectory.find(
      (item) => item.fullName === candidateName,
    );

    if (exactMatch) {
      return exactMatch.email;
    }

    const partialMatch = candidateDirectory.find((item) =>
      item.fullName.startsWith(candidateName),
    );

    return partialMatch ? partialMatch.email : "";
  };

  const filteredCandidates = useMemo(() => {
    const keyword = form.fullName.trim().toLowerCase();
    const scheduledCandidateIds = new Set(
      scheduledInterviews.map((item) =>
        normalizeCandidateId(item.applicationId || item.id),
      ),
    );
    Object.keys(pendingScheduledIds).forEach((candidateId) => {
      if (pendingScheduledIds[candidateId]) {
        scheduledCandidateIds.add(candidateId);
      }
    });
    const shortlistedCandidates = candidateDirectory.filter((item) => {
      const candidateId = normalizeCandidateId(item.applicationId || item.id);

      return (
        String(item.status || "").toUpperCase() === "SHORTLISTED" &&
        !scheduledCandidateIds.has(candidateId)
      );
    });

    if (!keyword) {
      return shortlistedCandidates;
    }

    return shortlistedCandidates.filter((item) =>
      item.fullName.toLowerCase().includes(keyword),
    );
  }, [
    candidateDirectory,
    form.fullName,
    pendingScheduledIds,
    scheduledInterviews,
  ]);

  const shouldShowCandidateMenu =
    !form.isManualSchedule &&
    isCandidateMenuOpen &&
    filteredCandidates.length > 0;

  const pipelineCandidates = useMemo(() => {
    const applicationEntries = applicationManagementCandidates
      .map((item) => {
        const applicationId = normalizeCandidateId(
          item.applicationId || item.id,
        );
        const storedPipelineEntry = pipelineState[applicationId] || {};
        const currentRound = getCurrentRoundKeyForApplication(item);
        const currentInterview = getInterviewForRound(item, currentRound);
        const currentDecision = getCurrentRoundDecision(item, currentRound);
        const writtenInterview = getInterviewForRound(item, ROUND_KEYS.WRITTEN);
        const technicalInterview = getInterviewForRound(
          item,
          ROUND_KEYS.TECHNICAL,
        );
        const hrInterview = getInterviewForRound(item, ROUND_KEYS.HR);

        return {
          applicationId,
          fullName: item.fullName,
          email: item.email,
          status: item.status || "SHORTLISTED",
          round: currentRound,
          decision: currentDecision,
          currentInterview,
          writtenInterview,
          technicalInterview,
          hrInterview,
          writtenInterviewScheduled:
            storedPipelineEntry.writtenInterviewScheduled ??
            Boolean(writtenInterview),
          writtenInterviewId:
            storedPipelineEntry.writtenInterviewId ||
            getInterviewId(writtenInterview),
          writtenInterviewDate:
            storedPipelineEntry.writtenInterviewDate ||
            writtenInterview?.scheduledDate ||
            writtenInterview?.rawDate ||
            "",
          writtenInterviewTime:
            storedPipelineEntry.writtenInterviewTime ||
            writtenInterview?.scheduledTime ||
            writtenInterview?.time ||
            "",
          writtenInterviewType:
            storedPipelineEntry.writtenInterviewType ||
            writtenInterview?.type ||
            "",
          writtenInterviewLink:
            storedPipelineEntry.writtenInterviewLink ||
            getInterviewLinkForRound(
              ROUND_KEYS.WRITTEN,
              writtenInterview || {},
            ),
          writtenInterviewLocation:
            storedPipelineEntry.writtenInterviewLocation ||
            writtenInterview?.location ||
            "",
          technicalInterviewScheduled:
            storedPipelineEntry.technicalInterviewScheduled ??
            Boolean(technicalInterview),
          technicalInterviewId:
            storedPipelineEntry.technicalInterviewId ||
            getInterviewId(technicalInterview),
          technicalInterviewDate:
            storedPipelineEntry.technicalInterviewDate ||
            technicalInterview?.scheduledDate ||
            technicalInterview?.rawDate ||
            "",
          technicalInterviewTime:
            storedPipelineEntry.technicalInterviewTime ||
            technicalInterview?.scheduledTime ||
            technicalInterview?.time ||
            "",
          technicalInterviewType:
            storedPipelineEntry.technicalInterviewType ||
            technicalInterview?.type ||
            "",
          technicalInterviewLink:
            storedPipelineEntry.technicalInterviewLink ||
            getInterviewLinkForRound(
              ROUND_KEYS.TECHNICAL,
              technicalInterview || {},
            ),
          technicalInterviewLocation:
            storedPipelineEntry.technicalInterviewLocation ||
            technicalInterview?.location ||
            "",
          hrInterviewScheduled:
            storedPipelineEntry.hrInterviewScheduled ?? Boolean(hrInterview),
          hrInterviewId:
            storedPipelineEntry.hrInterviewId || getInterviewId(hrInterview),
          hrInterviewDate:
            storedPipelineEntry.hrInterviewDate ||
            hrInterview?.scheduledDate ||
            hrInterview?.rawDate ||
            "",
          hrInterviewTime:
            storedPipelineEntry.hrInterviewTime ||
            hrInterview?.scheduledTime ||
            hrInterview?.time ||
            "",
          hrInterviewType:
            storedPipelineEntry.hrInterviewType || hrInterview?.type || "",
          hrInterviewLink:
            storedPipelineEntry.hrInterviewLink ||
            getInterviewLinkForRound(ROUND_KEYS.HR, hrInterview || {}),
          hrInterviewLocation:
            storedPipelineEntry.hrInterviewLocation ||
            hrInterview?.location ||
            "",
          updatedAt: storedPipelineEntry.updatedAt || 0,
        };
      })
      .filter((candidate) => {
        const status = String(candidate.status || "").toUpperCase();
        return (
          status === "SHORTLISTED" ||
          candidate.round !== ROUND_KEYS.WRITTEN ||
          candidate.writtenInterviewScheduled
        );
      });

    const manualEntries = manualInterviews.map((item) => {
      const writtenRecord =
        item.writtenRecord ||
        (isManualRoundScheduled(item, ROUND_KEYS.WRITTEN) ? item : null);
      const technicalRecord =
        item.technicalRecord ||
        (isManualRoundScheduled(item, ROUND_KEYS.TECHNICAL) ? item : null);
      const hrRecord =
        item.hrRecord ||
        (isManualRoundScheduled(item, ROUND_KEYS.HR) ? item : null);
      const isWrittenScheduled = Boolean(writtenRecord);
      const isTechnicalScheduled = Boolean(technicalRecord);
      const isHrScheduled = Boolean(hrRecord);

      return {
        applicationId: item.applicationId,
        fullName: item.fullName || "Candidate",
        email: item.email || "",
        jobTitle: item.jobTitle || "",
        status: "MANUAL",
        round: item.round || ROUND_KEYS.WRITTEN,
        decision: item.decision || "",
        isManualSchedule: true,
        writtenInterviewScheduled: isWrittenScheduled,
        writtenInterviewId: isWrittenScheduled
          ? writtenRecord.interviewId || ""
          : "",
        writtenInterviewDate: isWrittenScheduled
          ? writtenRecord.date || ""
          : "",
        writtenInterviewTime: isWrittenScheduled
          ? writtenRecord.time || ""
          : "",
        writtenInterviewType: isWrittenScheduled
          ? writtenRecord.type || ""
          : "",
        writtenInterviewLink: isWrittenScheduled
          ? writtenRecord.meetLink || ""
          : "",
        writtenInterviewLocation: isWrittenScheduled
          ? writtenRecord.location || ""
          : "",
        technicalInterviewScheduled: isTechnicalScheduled,
        technicalInterviewId: isTechnicalScheduled
          ? technicalRecord.interviewId || ""
          : "",
        technicalInterviewDate: isTechnicalScheduled
          ? technicalRecord.date || ""
          : "",
        technicalInterviewTime: isTechnicalScheduled
          ? technicalRecord.time || ""
          : "",
        technicalInterviewType: isTechnicalScheduled
          ? technicalRecord.type || ""
          : "",
        technicalInterviewLink: isTechnicalScheduled
          ? technicalRecord.meetLink || ""
          : "",
        technicalInterviewLocation: isTechnicalScheduled
          ? technicalRecord.location || ""
          : "",
        hrInterviewScheduled: isHrScheduled,
        hrInterviewId: isHrScheduled ? hrRecord.interviewId || "" : "",
        hrInterviewDate: isHrScheduled ? hrRecord.date || "" : "",
        hrInterviewTime: isHrScheduled ? hrRecord.time || "" : "",
        hrInterviewType: isHrScheduled ? hrRecord.type || "" : "",
        hrInterviewLink: isHrScheduled ? hrRecord.meetLink || "" : "",
        hrInterviewLocation: isHrScheduled ? hrRecord.location || "" : "",
        updatedAt: item.updatedAt || 0,
      };
    });

    return [...manualEntries, ...applicationEntries].sort((left, right) => {
      const leftRank =
        left.round === ROUND_KEYS.WRITTEN
          ? 0
          : left.round === ROUND_KEYS.TECHNICAL
            ? 1
            : 2;
      const rightRank =
        right.round === ROUND_KEYS.WRITTEN
          ? 0
          : right.round === ROUND_KEYS.TECHNICAL
            ? 1
            : 2;

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      return String(left.fullName || "").localeCompare(
        String(right.fullName || ""),
      );
    });
  }, [applicationManagementCandidates, manualInterviews, pipelineState]);

  const getRoundCandidates = (roundKey) =>
    pipelineCandidates
      .filter((candidate) => candidate.round === roundKey)
      .sort((left, right) => {
        const leftRejected = left.decision === "REJECTED" ? 1 : 0;
        const rightRejected = right.decision === "REJECTED" ? 1 : 0;

        if (leftRejected !== rightRejected) {
          return leftRejected - rightRejected;
        }

        const leftUpdatedAt = Number(left.updatedAt || 0);
        const rightUpdatedAt = Number(right.updatedAt || 0);

        if (leftUpdatedAt !== rightUpdatedAt) {
          return rightUpdatedAt - leftUpdatedAt;
        }

        return String(left.fullName || "").localeCompare(
          String(right.fullName || ""),
        );
      });

  const getFilteredRoundCandidates = (roundKey) => {
    const candidates = getRoundCandidates(roundKey);
    const keyword = String(roundSearchTerms[roundKey] || "").trim().toLowerCase();

    if (!keyword) {
      return candidates;
    }

    return candidates.filter((candidate) =>
      [candidate.fullName, candidate.email]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword)),
    );
  };

  const getInterviewDetailsForRound = (candidate, roundKey) => {
    if (roundKey === ROUND_KEYS.WRITTEN) {
      const writtenInterview = {
        interviewId: candidate.writtenInterviewId || "",
        rawDate: candidate.writtenInterviewDate || "",
        time: candidate.writtenInterviewTime || "",
        type: candidate.writtenInterviewType || "",
        meetLink: candidate.writtenInterviewLink || "",
        location: candidate.writtenInterviewLocation || "",
      };

      if (
        !candidate.writtenInterviewScheduled &&
        !hasInterviewDetails(writtenInterview)
      ) {
        if (
          candidate.interview &&
          getRoundKeyFromType(candidate.interview.roundType) ===
            ROUND_KEYS.WRITTEN
        ) {
          return {
            interviewId: getInterviewId(candidate.interview),
            rawDate:
              candidate.interview.scheduledDate ||
              candidate.interview.rawDate ||
              "",
            time:
              candidate.interview.scheduledTime ||
              candidate.interview.time ||
              "",
            type: candidate.interview.type || "",
            meetLink: getInterviewLinkForRound(
              ROUND_KEYS.WRITTEN,
              candidate.interview,
            ),
            location: candidate.interview.location || "",
          };
        }

        return null;
      }

      return writtenInterview;
    }

    if (roundKey === ROUND_KEYS.TECHNICAL) {
      if (!candidate.technicalInterviewScheduled) {
        return null;
      }

      return {
        interviewId: candidate.technicalInterviewId || "",
        rawDate: candidate.technicalInterviewDate || "",
        time: candidate.technicalInterviewTime || "",
        type: candidate.technicalInterviewType || "",
        meetLink: candidate.technicalInterviewLink || "",
        location: candidate.technicalInterviewLocation || "",
      };
    }

    if (roundKey === ROUND_KEYS.HR) {
      if (!candidate.hrInterviewScheduled) {
        return null;
      }

      return {
        interviewId: candidate.hrInterviewId || "",
        rawDate: candidate.hrInterviewDate || "",
        time: candidate.hrInterviewTime || "",
        type: candidate.hrInterviewType || "",
        meetLink: candidate.hrInterviewLink || "",
        location: candidate.hrInterviewLocation || "",
      };
    }

    return candidate.interview
      ? {
          interviewId: getInterviewId(candidate.interview),
          rawDate:
            candidate.interview.scheduledDate ||
            candidate.interview.rawDate ||
            "",
          time:
            candidate.interview.scheduledTime || candidate.interview.time || "",
          type: candidate.interview.type || "",
          meetLink: getInterviewLinkForRound(
            getRoundKeyFromType(candidate.interview.roundType),
            candidate.interview,
          ),
          location: candidate.interview.location || "",
        }
      : null;
  };

  const showPipelineValidationPopup = (message) => {
    setSchedulePopupTitle("Action Required");
    setSchedulePopupMessage(message);
    setIsSchedulePopupOpen(true);
  };

  const handleChange = (e) => {
    const { checked, name, type, value } = e.target;

    setScheduleError("");
    setFieldErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    if (name === "isManualSchedule") {
      const isManualSchedule =
        type === "checkbox" ? checked : value === "MANUAL";
      setForm((prev) => ({
        ...prev,
        interviewId: "",
        applicationId: "",
        schedulingRound: "",
        isManualSchedule,
        jobTitle: "",
        fullName: "",
        email: "",
      }));
      setManualEmailDraft("");
      setIsCandidateMenuOpen(false);
      setFieldErrors((prev) => ({
        ...prev,
        fullName: "",
        email: "",
      }));
      return;
    }

    if (name === "fullName") {
      if (form.isManualSchedule) {
        return;
      }

      const matchedEmail = findCandidateEmail(value);
      setForm((prev) => ({
        ...prev,
        fullName: value,
        email: matchedEmail || "",
      }));
      setIsCandidateMenuOpen(true);
      return;
    }

    if (name === "hour") {
      const sanitizedValue = value.replace(/\D/g, "").slice(0, 2);
      setForm((prev) => ({ ...prev, hour: sanitizedValue }));
      return;
    }

    if (name === "minute") {
      const sanitizedValue = value.replace(/\D/g, "").slice(0, 2);
      setForm((prev) => ({ ...prev, minute: sanitizedValue }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCandidateSelect = (applicationId, fullName, email) => {
    setForm((prev) => ({
      ...prev,
      interviewId: "",
      applicationId,
      schedulingRound: ROUND_KEYS.WRITTEN,
      isManualSchedule: false,
      jobTitle: "",
      fullName,
      email: email || "",
    }));
    setFieldErrors((prev) => ({
      ...prev,
      fullName: "",
      email: "",
    }));
    setIsCandidateMenuOpen(false);
  };

  const fillFormForCandidate = (candidate) => {
    const interview = getInterviewDetailsForRound(candidate, candidate.round);
    const timeParts = getTimeParts(interview?.time || "");

    setForm({
      interviewId: getInterviewId(interview),
      applicationId: candidate.applicationId,
      schedulingRound: candidate.round,
      isManualSchedule: Boolean(candidate.isManualSchedule),
      jobTitle: candidate.jobTitle || "",
      fullName: candidate.fullName || "",
      email: candidate.email || "",
      date: normalizeDateForInput(
        interview?.scheduledDate || interview?.rawDate || interview?.date || "",
      ),
      hour: timeParts.hour || "",
      minute: timeParts.minute || "",
      meridiem: timeParts.meridiem || "AM",
      useGoogleMeet: interview ? interview.type !== "OFFLINE" : true,
      meetLink: interview?.meetLink || "",
      location: interview?.location || "",
    });
    setFieldErrors({});
    setScheduleError("");

    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const resetForm = () => {
    setForm({
      interviewId: "",
      applicationId: "",
      schedulingRound: "",
      isManualSchedule: false,
      jobTitle: "",
      fullName: "",
      email: "",
      date: "",
      hour: "",
      minute: "",
      meridiem: "AM",
      useGoogleMeet: true,
      meetLink: "",
      location: "",
    });
    setManualEmailDraft("");
  };

  const syncScheduledInterviewState = ({
    applicationId,
    interviewId,
    fullName,
    email,
    roundKey,
    date,
    time,
    type,
    meetLink,
    location,
  }) => {
    const normalizedApplicationId = normalizeCandidateId(applicationId);

    if (!normalizedApplicationId || !roundKey) {
      return;
    }

    setScheduledInterviews((prev) => {
      const nextInterview = {
        id: normalizedApplicationId,
        interviewId: interviewId || "",
        applicationId: normalizedApplicationId,
        fullName: fullName || "",
        email: email || "",
        roundType: ROUND_TYPE_BY_KEY[roundKey] || "",
        rawDate: date || "",
        date: getSafeDisplayDate(date || ""),
        time: time || "",
        type: type || "",
        meetLink: meetLink || "",
        location: location || "",
      };

      const remaining = prev.filter(
        (item) =>
          normalizeCandidateId(item.applicationId || item.id) !==
          normalizedApplicationId,
      );

      return [nextInterview, ...remaining];
    });
  };

  const buildInterviewTime = () =>
    `${form.hour.padStart(2, "0")}:${form.minute.padStart(2, "0")} ${form.meridiem}`;

  const activeSchedulingRound = form.schedulingRound || ROUND_KEYS.WRITTEN;
  const virtualLinkLabel = getLinkLabelForRound(activeSchedulingRound);
  const isWrittenRound = activeSchedulingRound === ROUND_KEYS.WRITTEN;

  const validateScheduleForm = () => {
    const errors = {};
    const committedManualEmailValue = mergeEmailValues(
      form.email,
      manualEmailDraft,
    );
    const manualEmails = parseEmailList(committedManualEmailValue);

    if (!form.isManualSchedule && !form.fullName.trim()) {
      errors.fullName = "Candidate name is required.";
    }

    if (!form.isManualSchedule && !form.applicationId) {
      errors.fullName = "Please select a shortlisted candidate from the list.";
    }

    if (form.isManualSchedule && !form.jobTitle.trim()) {
      errors.jobTitle = "Please select a job title.";
    }

    if (form.isManualSchedule && manualEmails.length === 0) {
      errors.email = "Enter at least one candidate email.";
    } else if (!form.isManualSchedule && !form.email.trim()) {
      errors.email = form.isManualSchedule
        ? "Enter at least one candidate email."
        : "Candidate email is required.";
    } else if (form.isManualSchedule) {
      const invalidEmails = manualEmails.filter(
        (email) => !isValidEmail(email),
      );

      if (invalidEmails.length > 0) {
        errors.email = `Invalid email: ${invalidEmails[0]}`;
      }
    } else if (!isValidEmail(form.email.trim())) {
      errors.email = "Enter a valid candidate email.";
    }

    if (!form.date) {
      errors.date = "Interview date is required.";
    } else {
      const interviewDateTime = getDateTimeValue(
        form.date,
        buildInterviewTime(),
      );

      if (!interviewDateTime || Number.isNaN(interviewDateTime.getTime())) {
        errors.date = "Interview date must be a valid future date.";
      } else if (interviewDateTime <= new Date()) {
        errors.date = "Interview date must be a valid future date.";
      }
    }

    if (!form.hour.trim()) {
      errors.hour = "Hour is required.";
    } else {
      const hour = Number(form.hour);

      if (Number.isNaN(hour) || hour < 1 || hour > 12) {
        errors.hour = "Enter hour between 1 and 12.";
      }
    }

    if (!form.minute.trim()) {
      errors.minute = "Minute is required.";
    } else {
      const minute = Number(form.minute);

      if (Number.isNaN(minute) || minute < 0 || minute > 59) {
        errors.minute = "Enter minute between 00 and 59.";
      }
    }

    if (!form.meridiem) {
      errors.meridiem = "Select AM or PM.";
    }

    if (form.useGoogleMeet && !form.meetLink.trim()) {
      errors.meetLink = `${virtualLinkLabel} is required for virtual ${activeSchedulingRound === ROUND_KEYS.WRITTEN ? `${FIRST_ROUND_LABEL} interviews` : "interviews"}.`;
    }

    if (!form.useGoogleMeet && !form.location.trim()) {
      errors.location =
        "Interview location is required for offline interviews.";
    }

    return errors;
  };

  const updatePipelineCandidate = (applicationId, nextState) => {
    const normalizedId = normalizeCandidateId(applicationId);

    if (!normalizedId) {
      return;
    }

    setPipelineState((prev) => ({
      ...prev,
      [normalizedId]: {
        ...prev[normalizedId],
        ...nextState,
        updatedAt: Date.now(),
      },
    }));
  };

  const isRoundInterviewCompleted = (candidate, roundKey = candidate.round) => {
    const interview = getInterviewDetailsForRound(candidate, roundKey);

    if (!interview) {
      return false;
    }

    const scheduledDateTime = getDateTimeValue(
      interview.rawDate || interview.date || "",
      interview.time || "",
    );

    if (!scheduledDateTime) {
      return false;
    }

    return scheduledDateTime.getTime() <= Date.now();
  };

  const isWrittenInterviewScheduled = (candidate) =>
    hasInterviewDetails(
      getInterviewDetailsForRound(candidate, ROUND_KEYS.WRITTEN),
    );

  const getRoundScheduleDetails = (candidate) => {
    const interview = getInterviewDetailsForRound(candidate, candidate.round);

    return interview
      ? {
          date: interview.rawDate || interview.date || "",
          scheduledDate: interview.rawDate || interview.date || "",
          scheduledTime: interview.time || "",
          time: interview.time || "",
          type: interview.type || "",
          link:
            interview.type === "VIRTUAL"
              ? interview.link || interview.meetLink || ""
              : "",
          location:
            interview.type === "OFFLINE" ? interview.location || "" : "",
          linkLabel: getLinkLabelForRound(candidate.round),
        }
      : null;
  };

  const updateCandidateResult = async (candidate, result) => {
    const interview = getInterviewDetailsForRound(candidate, candidate.round);

    if (!interview?.interviewId) {
      showPipelineValidationPopup(
        `Please schedule the ${getLinkLabelForRound(candidate.round).toLowerCase()} round before marking the result.`,
      );
      return false;
    }

    if (!isRoundInterviewCompleted(candidate, candidate.round)) {
      showPipelineValidationPopup(
        `${getLinkLabelForRound(candidate.round)} actions are available only after the scheduled interview time is completed.`,
      );
      return false;
    }

    if (candidate.isManualSchedule) {
      if (!candidate.jobTitle) {
        showPipelineValidationPopup(
          "Please reschedule this Candidate and select the job title before marking the result.",
        );
        return false;
      }

      try {
        const response = await updateManualInterviewResult({
          email: candidate.email,
          fullName: candidate.fullName || "Candidate",
          jobTitle: candidate.jobTitle,
          roundType: ROUND_TYPE_BY_KEY[candidate.round],
          result,
        });
        const nextRoundKey =
          result === "SELECTED"
            ? getRoundKeyFromType(response?.data?.nextRoundType) ||
              NEXT_ROUND_BY_KEY[candidate.round]
            : "";

        setManualInterviews((prev) =>
          prev.map((item) => {
            if (item.applicationId !== candidate.applicationId) {
              return item;
            }

            if (result === "SELECTED" && nextRoundKey) {
              return {
                ...item,
                round: nextRoundKey,
                decision: "",
                interviewId: "",
                scheduledRound: "",
                date: "",
                time: "",
                type: "",
                meetLink: "",
                location: "",
                updatedAt: Date.now(),
              };
            }

            return {
              ...item,
              decision: result,
              updatedAt: Date.now(),
            };
          }),
        );

        setSchedulePopupTitle("Status Updated");
        setSchedulePopupMessage(
          result === "SELECTED"
            ? nextRoundKey
              ? `Candidate cleared ${candidate.round === ROUND_KEYS.WRITTEN ? FIRST_ROUND_LABEL.toLowerCase() : COMMUNICATION_AND_TECHNICAL_ROUND_LABEL.toLowerCase()} and moved to the next stage.`
              : "Candidate marked as selected successfully."
            : "Candidate marked as rejected successfully.",
        );
        setIsSchedulePopupOpen(true);
        return true;
      } catch (error) {
        showPipelineValidationPopup(
          error.response?.data?.message ||
            "Failed to send manual interview result email. Please try again.",
        );
        return false;
      }
    }

    try {
      await updateInterviewResult(getInterviewId(interview), result);
      await fetchApplications();
      await fetchScheduledInterviews();
      updatePipelineCandidate(candidate.applicationId, {});

      setSchedulePopupTitle("Status Updated");
      setSchedulePopupMessage(
        result === "SELECTED"
          ? candidate.round === ROUND_KEYS.HR
            ? "Candidate marked as selected successfully."
            : `Candidate cleared ${candidate.round === ROUND_KEYS.WRITTEN ? FIRST_ROUND_LABEL.toLowerCase() : COMMUNICATION_AND_TECHNICAL_ROUND_LABEL.toLowerCase()} and moved to the next stage.`
          : "Candidate marked as rejected successfully.",
      );
      setIsSchedulePopupOpen(true);
      return true;
    } catch (error) {
      showPipelineValidationPopup(
        error.response?.data?.message ||
          "Failed to update candidate status. Please try again.",
      );
      return false;
    }
  };

  const moveCandidateToNextRound = async (candidate) => {
    await updateCandidateResult(candidate, "SELECTED");
  };

  const rejectCandidateInRound = async (candidate) => {
    try {
      await updateCandidateResult(candidate, "REJECTED");
    } catch (error) {
      showPipelineValidationPopup(
        error.response?.data?.message ||
          "Failed to update candidate status. Please try again.",
      );
    }
  };

  const finalizeHrDecision = async (candidate, decision) => {
    try {
      await updateCandidateResult(candidate, decision);
    } catch (error) {
      showPipelineValidationPopup(
        error.response?.data?.message ||
          "Failed to update candidate status. Please try again.",
      );
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    setScheduleError("");
    const errors = validateScheduleForm();

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setScheduleError("Please correct the highlighted interview details.");
      return;
    }

    setFieldErrors({});

    const normalizedApplicationId = normalizeCandidateId(form.applicationId);
    const storedRoundState = normalizedApplicationId
      ? pipelineState[normalizedApplicationId]
      : null;
    const resolvedInterviewId =
      form.interviewId ||
      (activeSchedulingRound === ROUND_KEYS.WRITTEN
        ? storedRoundState?.writtenInterviewId || ""
        : activeSchedulingRound === ROUND_KEYS.TECHNICAL
          ? storedRoundState?.technicalInterviewId || ""
          : activeSchedulingRound === ROUND_KEYS.HR
            ? storedRoundState?.hrInterviewId || ""
            : "");
    const roundAlreadyScheduled =
      activeSchedulingRound === ROUND_KEYS.WRITTEN
        ? Boolean(storedRoundState?.writtenInterviewScheduled)
        : activeSchedulingRound === ROUND_KEYS.TECHNICAL
          ? Boolean(storedRoundState?.technicalInterviewScheduled)
          : activeSchedulingRound === ROUND_KEYS.HR
            ? Boolean(storedRoundState?.hrInterviewScheduled)
            : false;

    const requestPayload = {
      interviewType: form.useGoogleMeet ? "VIRTUAL" : "OFFLINE",
      date: normalizeDateForInput(form.date),
      time: buildInterviewTime(),
      ...(form.useGoogleMeet && isWrittenRound && form.meetLink.trim()
        ? { examLink: form.meetLink.trim() }
        : {}),
      ...(form.useGoogleMeet && !isWrittenRound && form.meetLink.trim()
        ? { meetingLink: form.meetLink.trim() }
        : {}),
      ...(!form.useGoogleMeet && form.location.trim()
        ? { location: form.location.trim() }
        : {}),
    };

    const schedulePayload = {
      roundType: ROUND_TYPE_BY_KEY[activeSchedulingRound],
      interviewType: form.useGoogleMeet ? "VIRTUAL" : "OFFLINE",
      date: normalizeDateForInput(form.date),
      time: buildInterviewTime(),
      ...(form.useGoogleMeet && isWrittenRound && form.meetLink.trim()
        ? { examLink: form.meetLink.trim() }
        : {}),
      ...(form.useGoogleMeet && !isWrittenRound && form.meetLink.trim()
        ? { meetingLink: form.meetLink.trim() }
        : {}),
      ...(!form.useGoogleMeet && form.location.trim()
        ? { location: form.location.trim() }
        : {}),
    };

    if (form.isManualSchedule) {
      try {
        const roundKey = activeSchedulingRound || ROUND_KEYS.WRITTEN;
        const manualJobTitle = form.jobTitle.trim();
        const existingManualEntry = manualInterviews.find(
          (item) => item.applicationId === form.applicationId,
        );
        const committedManualEmailValue = mergeEmailValues(
          form.email,
          manualEmailDraft,
        );
        const manualResponse = await scheduleManualInterview({
          emails: Array.from(
            new Set(parseEmailList(committedManualEmailValue)),
          ),
          jobTitle: manualJobTitle,
          ...schedulePayload,
        });
        const responseInterviews = Array.isArray(manualResponse?.data)
          ? manualResponse.data
          : [];
        const now = Date.now();
        const manualEntries = responseInterviews.map((item, index) => ({
          applicationId:
            getManualCandidateKey({
              email: item.email || item.candidateEmail || "",
              jobTitle: manualJobTitle,
              id: item.applicationId || `manual-${now}-${index}`,
            }) || `manual-${now}-${index}`,
          interviewId: item.interviewId || `manual-${now}-${index}`,
          fullName: "Candidate",
          email: item.email || item.candidateEmail || "",
          jobTitle: manualJobTitle,
          round: roundKey,
          scheduledRound: roundKey,
          date: item.rawDate || normalizeDateForInput(form.date),
          time: item.time || buildInterviewTime(),
          type:
            item.interviewType || (form.useGoogleMeet ? "VIRTUAL" : "OFFLINE"),
          meetLink: form.useGoogleMeet
            ? item.examLink || item.meetingLink || form.meetLink.trim()
            : "",
          location: form.useGoogleMeet
            ? ""
            : item.location || form.location.trim(),
          updatedAt: now + index,
        }));
        const failedEmails = Array.isArray(manualResponse?.failedEmails)
          ? manualResponse.failedEmails
          : [];

        if (manualEntries.length === 0) {
          setScheduleError("Manual interview emails were not sent.");
          return;
        }

        if (existingManualEntry) {
          const nextManualEntry = {
            ...existingManualEntry,
            ...manualEntries[0],
            applicationId: existingManualEntry.applicationId,
            fullName: existingManualEntry.fullName || "Candidate",
            email: manualEntries[0].email || existingManualEntry.email,
            jobTitle: manualJobTitle,
            round: roundKey,
            scheduledRound: roundKey,
            decision: "",
            updatedAt: now,
          };

          setManualInterviews((prev) =>
            prev.map((item) =>
              item.applicationId === existingManualEntry.applicationId
                ? nextManualEntry
                : item,
            ),
          );
        } else {
          setManualInterviews((prev) => [...manualEntries, ...prev]);
        }
        setSchedulePopupTitle("Interview Scheduled");
        setSchedulePopupMessage(
          failedEmails.length
            ? `${manualEntries.length} manual interview email(s) sent. Failed: ${failedEmails.join(", ")}`
            : existingManualEntry
              ? "Manual interview rescheduled successfully."
              : manualEntries.length === 1
                ? "Manual interview email sent and scheduled successfully."
                : `${manualEntries.length} manual interview emails sent and scheduled successfully.`,
        );
        resetForm();
        setFieldErrors({});
        setScheduleError("");
        setIsSchedulePopupOpen(true);
      } catch (error) {
        setScheduleError(
          error.response?.data?.message ||
            "Failed to send manual interview emails.",
        );
        console.error("Manual interview scheduling failed", error);
      }
      return;
    }

    try {
      if (resolvedInterviewId) {
        const updateResponse = await updateInterview(
          resolvedInterviewId,
          requestPayload,
        );
        if (form.schedulingRound === ROUND_KEYS.TECHNICAL) {
          updatePipelineCandidate(form.applicationId, {
            round: ROUND_KEYS.TECHNICAL,
            decision: "",
            technicalInterviewScheduled: true,
            technicalInterviewId: resolvedInterviewId,
            technicalInterviewDate: form.date,
            technicalInterviewTime: buildInterviewTime(),
            technicalInterviewType:
              updateResponse?.data?.interviewType ||
              (form.useGoogleMeet ? "VIRTUAL" : "OFFLINE"),
            technicalInterviewLink: form.useGoogleMeet ? form.meetLink : "",
            technicalInterviewLocation: form.useGoogleMeet ? "" : form.location,
          });
        }
        if (form.schedulingRound === ROUND_KEYS.HR) {
          updatePipelineCandidate(form.applicationId, {
            round: ROUND_KEYS.HR,
            decision: "",
            hrInterviewScheduled: true,
            hrInterviewId: resolvedInterviewId,
            hrInterviewDate: form.date,
            hrInterviewTime: buildInterviewTime(),
            hrInterviewType:
              updateResponse?.data?.interviewType ||
              (form.useGoogleMeet ? "VIRTUAL" : "OFFLINE"),
            hrInterviewLink: form.useGoogleMeet ? form.meetLink : "",
            hrInterviewLocation: form.useGoogleMeet ? "" : form.location,
          });
        }
        if (form.schedulingRound === ROUND_KEYS.WRITTEN) {
          updatePipelineCandidate(form.applicationId, {
            round: ROUND_KEYS.WRITTEN,
            decision: "",
            writtenInterviewScheduled: true,
            writtenInterviewId: resolvedInterviewId,
            writtenInterviewDate: form.date,
            writtenInterviewTime: buildInterviewTime(),
            writtenInterviewType:
              updateResponse?.data?.interviewType ||
              (form.useGoogleMeet ? "VIRTUAL" : "OFFLINE"),
            writtenInterviewLink: form.useGoogleMeet ? form.meetLink : "",
            writtenInterviewLocation: form.useGoogleMeet ? "" : form.location,
          });
        }
        setSchedulePopupTitle("Interview Scheduled");
        setSchedulePopupMessage("Interview rescheduled successfully.");
      } else {
        if (roundAlreadyScheduled) {
          setScheduleError(
            "This round is already scheduled, but the interview ID is not available to reschedule it right now.",
          );
          return;
        }

        const scheduleResponse = await scheduleInterview(
          form.applicationId,
          schedulePayload,
        );
        setPendingScheduledIds((prev) => ({
          ...prev,
          [normalizeCandidateId(form.applicationId)]: true,
        }));
        updatePipelineCandidate(
          form.applicationId,
          form.schedulingRound === ROUND_KEYS.TECHNICAL
            ? {
                round: ROUND_KEYS.TECHNICAL,
                decision: "",
                technicalInterviewScheduled: true,
                technicalInterviewId: scheduleResponse?.data?.interviewId || "",
                technicalInterviewDate: form.date,
                technicalInterviewTime: buildInterviewTime(),
                technicalInterviewType:
                  scheduleResponse?.data?.interviewType ||
                  (form.useGoogleMeet ? "VIRTUAL" : "OFFLINE"),
                technicalInterviewLink: form.useGoogleMeet ? form.meetLink : "",
                technicalInterviewLocation: form.useGoogleMeet
                  ? ""
                  : form.location,
              }
            : form.schedulingRound === ROUND_KEYS.HR
              ? {
                  round: ROUND_KEYS.HR,
                  decision: "",
                  hrInterviewScheduled: true,
                  hrInterviewId: scheduleResponse?.data?.interviewId || "",
                  hrInterviewDate: form.date,
                  hrInterviewTime: buildInterviewTime(),
                  hrInterviewType:
                    scheduleResponse?.data?.interviewType ||
                    (form.useGoogleMeet ? "VIRTUAL" : "OFFLINE"),
                  hrInterviewLink: form.useGoogleMeet ? form.meetLink : "",
                  hrInterviewLocation: form.useGoogleMeet ? "" : form.location,
                }
              : {
                  round: ROUND_KEYS.WRITTEN,
                  decision: "",
                  writtenInterviewScheduled: true,
                  writtenInterviewId: scheduleResponse?.data?.interviewId || "",
                  writtenInterviewDate: form.date,
                  writtenInterviewTime: buildInterviewTime(),
                  writtenInterviewType:
                    scheduleResponse?.data?.interviewType ||
                    (form.useGoogleMeet ? "VIRTUAL" : "OFFLINE"),
                  writtenInterviewLink: form.useGoogleMeet ? form.meetLink : "",
                  writtenInterviewLocation: form.useGoogleMeet
                    ? ""
                    : form.location,
                },
        );
        syncScheduledInterviewState({
          applicationId: form.applicationId,
          interviewId: scheduleResponse?.data?.interviewId || "",
          fullName: form.fullName,
          email: form.email,
          roundKey: form.schedulingRound || ROUND_KEYS.WRITTEN,
          date: form.date,
          time: buildInterviewTime(),
          type:
            scheduleResponse?.data?.interviewType ||
            (form.useGoogleMeet ? "VIRTUAL" : "OFFLINE"),
          meetLink: form.useGoogleMeet ? form.meetLink : "",
          location: form.useGoogleMeet ? "" : form.location,
        });
        setSchedulePopupTitle("Interview Scheduled");
        setSchedulePopupMessage("Interview scheduled successfully.");
      }

      resetForm();
      setFieldErrors({});
      setScheduleError("");
      await Promise.all([fetchApplications(), fetchScheduledInterviews()]);
      setIsSchedulePopupOpen(true);
    } catch (error) {
      setScheduleError(
        error.response?.data?.message || "Failed to schedule interview.",
      );
      console.error("Interview scheduling failed", error);
    }
  };

  const renderPipelineActions = (candidate) => {
    if (
      candidate.decision === "REJECTED" ||
      candidate.decision === "SELECTED"
    ) {
      return null;
    }

    const isRoundScheduled =
      candidate.round === ROUND_KEYS.WRITTEN
        ? candidate.writtenInterviewScheduled
        : candidate.round === ROUND_KEYS.TECHNICAL
          ? candidate.technicalInterviewScheduled
          : candidate.hrInterviewScheduled;

    const roundCompleted = isRoundInterviewCompleted(
      candidate,
      candidate.round,
    );

    if (!candidate.isManualSchedule && !isRoundScheduled) {
      return (
        <div className="pipeline-card-actions">
          <button
            type="button"
            className="pipeline-action-btn schedule"
            onClick={() => fillFormForCandidate(candidate)}
          >
            Schedule
          </button>
        </div>
      );
    }

    if (candidate.round === ROUND_KEYS.WRITTEN) {
      return (
        <div className="pipeline-card-actions">
          <button
            type="button"
            className="pipeline-action-btn schedule"
            onClick={() => fillFormForCandidate(candidate)}
          >
            {isWrittenInterviewScheduled(candidate) ? "Reschedule" : "Schedule"}
          </button>
          <button
            type="button"
            className="pipeline-action-btn select"
            onClick={() => {
              if (!isRoundScheduled) {
                showPipelineValidationPopup(
                  "Please schedule the First Round before selecting this candidate.",
                );
                return;
              }

              if (!roundCompleted) {
                showPipelineValidationPopup(
                  "First Round actions are available only after the scheduled interview time is completed.",
                );
                return;
              }

              moveCandidateToNextRound(candidate);
            }}
          >
            Selected
          </button>
          <button
            type="button"
            className="pipeline-action-btn reject"
            onClick={() => {
              if (!isRoundScheduled) {
                showPipelineValidationPopup(
                  "Please schedule the First Round before rejecting this candidate.",
                );
                return;
              }

              if (!roundCompleted) {
                showPipelineValidationPopup(
                  "First Round actions are available only after the scheduled interview time is completed.",
                );
                return;
              }

              rejectCandidateInRound(candidate);
            }}
          >
            Rejected
          </button>
        </div>
      );
    }

    if (candidate.round === ROUND_KEYS.TECHNICAL) {
      return (
        <div className="pipeline-card-actions">
          <button
            type="button"
            className="pipeline-action-btn schedule"
            onClick={() => fillFormForCandidate(candidate)}
          >
            {candidate.technicalInterviewScheduled ? "Reschedule" : "Schedule"}
          </button>
          <button
            type="button"
            className="pipeline-action-btn select"
            onClick={() => {
              if (!isRoundScheduled) {
                showPipelineValidationPopup(
                  `Please schedule the ${COMMUNICATION_AND_TECHNICAL_ROUND_LABEL.toLowerCase()} interview before selecting this candidate.`,
                );
                return;
              }

              if (!roundCompleted) {
                showPipelineValidationPopup(
                  `${COMMUNICATION_AND_TECHNICAL_ROUND_LABEL} actions are available only after the scheduled interview time is completed.`,
                );
                return;
              }

              moveCandidateToNextRound(candidate);
            }}
          >
            Selected
          </button>
          <button
            type="button"
            className="pipeline-action-btn reject"
            onClick={() => {
              if (!isRoundScheduled) {
                showPipelineValidationPopup(
                  `Please schedule the ${COMMUNICATION_AND_TECHNICAL_ROUND_LABEL.toLowerCase()} interview before rejecting this candidate.`,
                );
                return;
              }

              if (!roundCompleted) {
                showPipelineValidationPopup(
                  `${COMMUNICATION_AND_TECHNICAL_ROUND_LABEL} actions are available only after the scheduled interview time is completed.`,
                );
                return;
              }

              rejectCandidateInRound(candidate);
            }}
          >
            Rejected
          </button>
        </div>
      );
    }

    return (
      <div className="pipeline-card-actions">
        <button
          type="button"
          className="pipeline-action-btn schedule"
          onClick={() => fillFormForCandidate(candidate)}
        >
          {candidate.hrInterviewScheduled ? "Reschedule" : "Schedule"}
        </button>
        <button
          type="button"
          className="pipeline-action-btn select"
          onClick={() => {
            if (!isRoundScheduled) {
              showPipelineValidationPopup(
                "Please schedule the HR round interview before selecting this candidate.",
              );
              return;
            }

            if (!roundCompleted) {
              showPipelineValidationPopup(
                "HR round actions are available only after the scheduled interview time is completed.",
              );
              return;
            }

            finalizeHrDecision(candidate, "SELECTED");
          }}
        >
          Selected
        </button>
        <button
          type="button"
          className="pipeline-action-btn reject"
          onClick={() => {
            if (!isRoundScheduled) {
              showPipelineValidationPopup(
                "Please schedule the HR round interview before rejecting this candidate.",
              );
              return;
            }

            if (!roundCompleted) {
              showPipelineValidationPopup(
                "HR round actions are available only after the scheduled interview time is completed.",
              );
              return;
            }

            finalizeHrDecision(candidate, "REJECTED");
          }}
        >
          Rejected
        </button>
      </div>
    );
  };

  const handleRoundColumnWheel = (e) => {
    const element = e.currentTarget;

    if (element.scrollHeight <= element.clientHeight) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    element.scrollTop += e.deltaY;
  };

  const handleCandidateSuggestionsWheel = (e) => {
    const element = e.currentTarget;

    if (element.scrollHeight <= element.clientHeight) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    element.scrollTop += e.deltaY;
  };

  return (
    <section className="interview-page">
      <article className="hr-page-card">
        <div className="hr-page-title">
          <CalendarCheck2 size={20} />
          <h2>Interview Scheduling</h2>
        </div>

        <form
          className="interview-form"
          onSubmit={handleSchedule}
          autoComplete="off"
        >
          <div className="interview-field schedule-mode-field">
            <label
              className={`schedule-mail-checkbox ${form.isManualSchedule ? "checked" : ""}`}
            >
              <input
                type="checkbox"
                name="isManualSchedule"
                checked={form.isManualSchedule}
                onChange={handleChange}
              />
              <span className="schedule-mail-checkbox-box" aria-hidden="true" />
              <span className="schedule-mail-checkbox-text">
                Manual interview Schedule
              </span>
            </label>
          </div>

          {form.isManualSchedule && (
            <div className="interview-field manual-job-title-field">
              {form.applicationId && form.jobTitle ? (
                <input
                  type="text"
                  name="jobTitle"
                  value={form.jobTitle}
                  readOnly
                  className={fieldErrors.jobTitle ? "input-error" : ""}
                  placeholder="Job Title"
                />
              ) : (
                <select
                  name="jobTitle"
                  value={form.jobTitle}
                  onChange={handleChange}
                  className={fieldErrors.jobTitle ? "input-error" : ""}
                >
                  <option value="">Select Job Title</option>
                  {jobOptions.map((jobTitle, index) => (
                    <option key={`${jobTitle}-${index}`} value={jobTitle}>
                      {jobTitle}
                    </option>
                  ))}
                </select>
              )}
              {fieldErrors.jobTitle && (
                <p className="field-error-message">{fieldErrors.jobTitle}</p>
              )}
            </div>
          )}

          <div className="candidate-input-wrap" ref={candidateInputWrapRef}>
            <input
              name="fullName"
              autoComplete="off"
              spellCheck="false"
              value={form.fullName}
              onChange={handleChange}
              className={fieldErrors.fullName ? "input-error" : ""}
              disabled={form.isManualSchedule}
              onFocus={() => {
                if (!form.isManualSchedule) {
                  setIsCandidateMenuOpen(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => {
                  const recentlyUsedCandidateMenu =
                    Date.now() - lastCandidateMenuInteractionRef.current < 300;

                  if (
                    isCandidateMenuInteractionRef.current ||
                    recentlyUsedCandidateMenu
                  ) {
                    return;
                  }

                  setIsCandidateMenuOpen(false);
                }, 160);
              }}
              placeholder={
                form.isManualSchedule
                  ? "Candidate Name disabled for manual schedule"
                  : "Candidate Name"
              }
            />
            <div
              className={`candidate-suggestions ${shouldShowCandidateMenu ? "show" : ""}`}
              onMouseDownCapture={noteCandidateMenuInteraction}
              onMouseUpCapture={releaseCandidateMenuInteraction}
              onMouseLeave={releaseCandidateMenuInteraction}
              onTouchStartCapture={noteCandidateMenuInteraction}
              onTouchEndCapture={releaseCandidateMenuInteraction}
            >
              <span>Click candidate name to auto-fill email:</span>
              <div
                className="candidate-chips"
                onWheelCapture={handleCandidateSuggestionsWheel}
              >
                {filteredCandidates.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    className="candidate-chip"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleCandidateSelect(
                        item.applicationId,
                        item.fullName,
                        item.email,
                      );
                    }}
                  >
                    {item.fullName}
                  </button>
                ))}
              </div>
            </div>
            {fieldErrors.fullName && (
              <p className="field-error-message">{fieldErrors.fullName}</p>
            )}
          </div>

          <div className="interview-field">
            {form.isManualSchedule ? (
              <div className="manual-email-field">
                <span className="manual-email-label">
                  <AtSign size={14} />
                  To
                </span>
                <ManualEmailChipsInput
                  value={form.email}
                  draftValue={manualEmailDraft}
                  onDraftChange={setManualEmailDraft}
                  onChange={handleChange}
                  hasError={Boolean(fieldErrors.email)}
                />
              </div>
            ) : (
              <input
                type="email"
                name="email"
                autoComplete="off"
                spellCheck="false"
                value={form.email}
                onChange={handleChange}
                className={fieldErrors.email ? "input-error" : ""}
                placeholder="Candidate Email"
              />
            )}
            {fieldErrors.email && (
              <p className="field-error-message">{fieldErrors.email}</p>
            )}
          </div>

          <div className="interview-field">
            <input
              type="date"
              name="date"
              autoComplete="off"
              value={form.date}
              onChange={handleChange}
              className={fieldErrors.date ? "input-error" : ""}
              min={getTodayDateValue()}
            />
            {fieldErrors.date && (
              <p className="field-error-message">{fieldErrors.date}</p>
            )}
          </div>

          <div className="time-input-wrap">
            <div className="time-entry-row">
              <DropdownTextInput
                name="hour"
                value={form.hour}
                placeholder="HH"
                options={HOUR_OPTIONS}
                onChange={handleChange}
                hasError={Boolean(fieldErrors.hour)}
              />
              <span className="time-separator">:</span>
              <DropdownTextInput
                name="minute"
                value={form.minute}
                placeholder="MM"
                options={MINUTE_OPTIONS}
                onChange={handleChange}
                hasError={Boolean(fieldErrors.minute)}
              />
              <select
                name="meridiem"
                value={form.meridiem}
                onChange={handleChange}
                className={fieldErrors.meridiem ? "input-error" : ""}
              >
                {MERIDIEM_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            {(fieldErrors.hour ||
              fieldErrors.minute ||
              fieldErrors.meridiem) && (
              <p className="field-error-message">
                {fieldErrors.hour || fieldErrors.minute || fieldErrors.meridiem}
              </p>
            )}
          </div>

          <div className="mode-row">
            <div className="mode-dropdown-wrap">
              <select
                className="mode-dropdown"
                value={form.useGoogleMeet ? "VIRTUAL" : "OFFLINE"}
                onChange={(e) => {
                  const isVirtual = e.target.value === "VIRTUAL";
                  setFieldErrors((prev) => ({
                    ...prev,
                    meetLink: "",
                    location: "",
                  }));
                  setForm((prev) => ({
                    ...prev,
                    useGoogleMeet: isVirtual,
                  }));
                }}
              >
                <option value="VIRTUAL">VIRTUAL</option>
                <option value="OFFLINE">OFFLINE</option>
              </select>
            </div>
            <div className="mode-detail-field">
              {form.useGoogleMeet ? (
                <input
                  type="text"
                  name="meetLink"
                  autoComplete="off"
                  value={form.meetLink}
                  onChange={handleChange}
                  className={`mode-detail-input ${fieldErrors.meetLink ? "input-error" : ""}`}
                  placeholder={virtualLinkLabel}
                />
              ) : (
                <input
                  name="location"
                  autoComplete="off"
                  value={form.location}
                  onChange={handleChange}
                  className={`mode-detail-input ${fieldErrors.location ? "input-error" : ""}`}
                  placeholder="Interview Location"
                />
              )}
              {form.useGoogleMeet && fieldErrors.meetLink && (
                <p className="field-error-message">{fieldErrors.meetLink}</p>
              )}
              {!form.useGoogleMeet && fieldErrors.location && (
                <p className="field-error-message">{fieldErrors.location}</p>
              )}
            </div>
          </div>

          <button type="submit" className="schedule-btn">
            {form.interviewId ? "Update Interview" : "Schedule Interview"}
          </button>
          {scheduleError && <p className="error-message">{scheduleError}</p>}
        </form>
      </article>

      <article className="hr-page-card">
        <div className="hr-page-title">
          <CalendarCheck2 size={20} />
          <h2>Scheduled Interviews</h2>
        </div>

        <div
          className="pipeline-board"
          style={{
            alignItems: "stretch",
            maxHeight: "78vh",
            overflow: "hidden",
          }}
        >
          {PIPELINE_COLUMNS.map((column) => {
            const allCandidates = getRoundCandidates(column.key);
            const candidates = getFilteredRoundCandidates(column.key);
            const searchTerm = roundSearchTerms[column.key] || "";

            return (
              <section
                className="pipeline-column"
                key={column.key}
                style={{
                  minHeight: 0,
                  maxHeight: "78vh",
                  overflow: "hidden",
                }}
              >
                <div className="pipeline-column-header">
                  <div>
                    <h3>{column.title}</h3>
                    <p>{column.description}</p>
                  </div>
                  <span className="pipeline-count">{candidates.length}</span>
                </div>

                <label className="pipeline-search" aria-label={`Search ${column.title} candidates`}>
                  <Search size={16} aria-hidden="true" />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(event) =>
                      setRoundSearchTerms((prev) => ({
                        ...prev,
                        [column.key]: event.target.value,
                      }))
                    }
                    placeholder="Search name or email"
                  />
                </label>

                <div
                  className="pipeline-card-list"
                  onWheel={handleRoundColumnWheel}
                  style={{
                    minHeight: 0,
                    maxHeight: "calc(78vh - 148px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                    paddingRight: "4px",
                  }}
                >
                  {candidates.length === 0 && (
                    <div className="pipeline-empty-card">
                      {searchTerm.trim()
                        ? "No candidate matched this search."
                        : allCandidates.length === 0
                          ? "No candidates in this round."
                          : "No candidate matched this search."}
                    </div>
                  )}

                  {candidates.map((candidate) => (
                    <article
                      className="pipeline-card"
                      key={`${candidate.applicationId}-${candidate.round}-${candidate.interview?.interviewId || candidate.interview?.id || candidate.fullName}`}
                    >
                      {(() => {
                        const scheduleDetails =
                          getRoundScheduleDetails(candidate);

                        return (
                          <>
                            <div className="pipeline-card-top">
                              <div>
                                <h4>{candidate.fullName}</h4>
                                <p>
                                  {candidate.email || "Email not available"}
                                </p>
                              </div>
                              {candidate.decision && (
                                <span
                                  className={`pipeline-decision-badge ${candidate.decision.toLowerCase()}`}
                                >
                                  {candidate.decision}
                                </span>
                              )}
                            </div>

                            {scheduleDetails ? (
                              <div className="pipeline-card-meta">
                                <span>
                                  Scheduled on{" "}
                                  {getSafeDisplayDate(
                                    scheduleDetails.scheduledDate ||
                                      scheduleDetails.date,
                                  )}
                                  {scheduleDetails.scheduledTime ||
                                  scheduleDetails.time
                                    ? ` at ${scheduleDetails.scheduledTime || scheduleDetails.time}`
                                    : ""}
                                </span>
                                <span>
                                  {scheduleDetails.type || "Scheduled"}
                                </span>
                              </div>
                            ) : (
                              <p className="pipeline-card-note">
                                {candidate.round === ROUND_KEYS.TECHNICAL &&
                                candidate.technicalInterviewScheduled
                                  ? "Interview scheduled. Waiting for results."
                                  : candidate.round === ROUND_KEYS.HR &&
                                      candidate.hrInterviewScheduled
                                    ? "Interview scheduled. Waiting for results."
                                    : candidate.round === ROUND_KEYS.WRITTEN
                                      ? "First Round not scheduled yet."
                                      : "Interview not scheduled yet."}
                              </p>
                            )}

                            {scheduleDetails?.type === "OFFLINE" &&
                              scheduleDetails.location && (
                                <p className="pipeline-interview-extra">
                                  Location: {scheduleDetails.location}
                                </p>
                              )}

                            {renderPipelineActions(candidate)}
                          </>
                        );
                      })()}
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </article>

      {isSchedulePopupOpen && (
        <div
          className="schedule-popup-overlay"
          onClick={() => setIsSchedulePopupOpen(false)}
        >
          <div
            className="schedule-popup-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Interview scheduled popup"
          >
            <h3>{schedulePopupTitle}</h3>
            <p>{schedulePopupMessage}</p>
            <button
              type="button"
              className="schedule-popup-btn"
              onClick={() => setIsSchedulePopupOpen(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default HRInterviewSchedulingPage;
